import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { ExternalLinkService } from './external-link.service';
import { GroupService } from './group.service';

import { SUPPORT_USER_ID } from '../utils/support-user';

export { SUPPORT_USER_ID };
const SUPPORT_WHATSAPP_NUMBER = '27816079006';
const SUPPORT_INVITE_EMAIL = 'contact@medicai.co.za';
const SUPPORT_INVITE_NAME = 'MedicAI Support';
const SUPPORT_INVITE_ROLE = 'Admin';

@Injectable({
  providedIn: 'root'
})
export class SupportService {

  constructor(
    private groupService: GroupService,
    private externalLink: ExternalLinkService,
  ) {}

  /**
   * Build a templated support message from user and group info for WhatsApp.
   */
  buildSupportMessage(user: any, group: any): string {
    const parts: string[] = ['Hi MedicAI Support, I need assistance.', ''];
    if (user) {
      const name = [user.title, user.name, user.surname].filter(Boolean).join(' ').trim() || 'N/A';
      const email = user.email || 'N/A';
      parts.push(`User: ${name} (${email})`);
    }
    if (group) {
      parts.push(`Practice/Group: ${group.name || 'N/A'}`);
    }
    return parts.join('\n');
  }

  /**
   * WhatsApp click-to-chat link to the support number with the message pre-filled.
   */
  buildSupportUrl(user: any, group: any): string {
    const message = this.buildSupportMessage(user, group);
    return `https://wa.me/${SUPPORT_WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
  }

  /**
   * Open WhatsApp to the support number with a pre-filled message, and make sure the
   * Dr MedicAI Support user is in the current group (invite if not).
   *
   * The link is opened first, synchronously with the click that triggered it: iOS
   * (Safari, an installed PWA and the WKWebView in the native app) drops the user
   * gesture across an HTTP round-trip and then silently blocks the link, so the group
   * membership check has to run after the link is opened, never before it.
   */
  openWhatsAppSupport(user: any, group: any): Observable<void> {
    this.externalLink.open(this.buildSupportUrl(user, group))
      .catch((error: any) => console.error('Failed to open WhatsApp support', error));
    return this.ensureSupportUserInGroup();
  }

  /**
   * Best-effort housekeeping behind the support chat — failures here must not surface
   * as "could not open WhatsApp", since WhatsApp has already been opened by then.
   */
  private ensureSupportUserInGroup(): Observable<void> {
    return this.groupService.getGroupUsers().pipe(
      take(1),
      switchMap((response: any) => {
        const users = response?.users ?? [];
        const hasSupportUser = users.some((u: any) => u.userId === SUPPORT_USER_ID);
        if (hasSupportUser) {
          return of(undefined);
        }
        return this.groupService.inviteUser(
          SUPPORT_INVITE_EMAIL,
          SUPPORT_INVITE_ROLE,
          SUPPORT_INVITE_NAME,
          true,
          true,
          true
        );
      }),
      catchError((error: any) => {
        console.error('Failed to add MedicAI Support to the group', error);
        return of(undefined);
      }),
      map(() => undefined)
    );
  }
}
